import { useMutation } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useCallback, useMemo, useState } from 'react'
import { Alert, Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { formatDateString } from 'utilities/time'
import { useTagList, LIST_TAGS } from '../../graphql/tagQueries'
import { DELETE_TAG } from '../../graphql/tagMutations'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const TagsTab = () => {
  const navigate = useNavigate()
  const { loading, error, data } = useTagList()
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const refetchConfig = { refetchQueries: [{ query: LIST_TAGS }] }

  const [deleteTag] = useMutation(DELETE_TAG, refetchConfig)

  const tags = useMemo(() => data?.tags || [], [data])

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return tags

    const query = searchQuery.toLowerCase()
    return tags.filter((tag) => {
      return (
        tag.tag.toLowerCase().includes(query) ||
        tag.id.toLowerCase().includes(query) ||
        tag.tagType.toLowerCase().includes(query) ||
        (tag.active ? 'active' : 'inactive').includes(query)
      )
    })
  }, [tags, searchQuery])

  const handleDelete = useCallback(
    async (tagId) => {
      await deleteTag({ variables: { input: { tagId } } })
      setDeleteConfirmId(null)
    },
    [deleteTag],
  )

  if (loading && !data) return <Loading />
  if (error)
    return <Alert severity="error">Failed to load tags: {error.message}</Alert>

  return (
    <Box>
      <AdminPageHeader>
        <Typography variant="h6">Tags ({filteredTags.length})</Typography>
        <Button
          type="primary"
          size="small"
          onClick={() => navigate(ROUTE_METADATA.adminOutreachTagNew.path)}
          startIcon={<AddIcon />}
        >
          New Tag
        </Button>
      </AdminPageHeader>

      <Paper elevation={1} sx={{ p: 2 }}>
        <TextField
          placeholder="Search tags by name, ID, type, or status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTags.map((tag) => (
                <TableRow
                  key={tag.id}
                  hover
                  onClick={() =>
                    navigate(
                      `${ROUTE_METADATA.adminOutreachTags.path}/${tag.id}`,
                    )
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {tag.tag}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tag.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={tag.tagType} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tag.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={tag.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {tag.startDate
                      ? formatDateString({
                          dateString: tag.startDate,
                          format: 'MMM DD, YYYY',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {tag.endDate
                      ? formatDateString({
                          dateString: tag.endDate,
                          format: 'MMM DD, YYYY',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(
                          `${ROUTE_METADATA.adminOutreachTags.path}/${tag.id}`,
                        )
                      }}
                      title="View Details"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteConfirmId(tag.id)
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure? This will permanently delete this tag and all
            associated data (patient tags, criteria, campaigns).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => handleDelete(deleteConfirmId)}
            sx={{ backgroundColor: 'error.main' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TagsTab
